import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { DefaultLanguage } from 'app/i18/i18'
import { RoutingPaths } from 'app/routes/routes'

import { getShareUrl } from 'entities/tour/ui/appeal-details/libs/getShareUrl'
import userStore from 'entities/user/module/user-store'

import { UserApi } from 'shared/api/UserApi'
import { openExternalUrl } from 'shared/lib/external-open'
import { getLocalStore } from 'shared/lib/local-store'
import { openWhatsappWithMessage } from 'shared/lib/openWhatsappWithMessage'
import { shareText } from 'shared/lib/share'
import { message } from 'shared/ui/message'

import s from './styles.module.scss'

const TouraikaSource = 'https://touraika.mtravel.kg'

const TouraikaOpenUrl = `${TouraikaSource}/?theme=mtravel-webview&device_id`

const DeviceId = ''

const TouraikaActions = {
  share: {
    key: 'share',
    action: 'touraika-share-tour',
  },
  close: {
    key: 'close',
    action: 'touraika-close',
  },
  book: {
    key: 'book',
    action: 'touraika-book-tour',
  },
  appeal: {
    key: 'appeal',
    action: 'touraika-tour-request',
  },
}

const langMap: Record<string, string> = { ky: 'kg', en: 'en', ru: 'ru' }

interface TouraikaMessage {
  type: string
  source: string
  theme: string
  session_id: string
  tour_id: string
  url?: string
}

const TouraikaPage = observer(() => {
  const navigate = useNavigate()

  const userStoreData = userStore.getAccountInfo()

  const { id } = useParams()

  const { t } = useTranslation()

  const iframe = useRef<HTMLIFrameElement>(null)

  // todo улучшить получение языка, сейчас так сделано для корректного отображения при переходе из баннера, который передает язык через стейт, и при прямом открытии страницы, когда язык берется из localStorage
  const locationState = useLocation()?.state as { lang: string } | null

  const storedLang = userStore.getClientStateString('language')

  const lang: string = locationState?.lang ?? storedLang ?? DefaultLanguage

  const handleShare = (shareUrl: string) => {
    shareText({
      url: shareUrl,
      onCopied: () => {
        message.copy(t('link_copied_to_clipboard'))
      },
    })
  }

  const onSentAppeal = async (session_id: string) => {
    try {
      await UserApi.sentAppealTouraika({ session_id })
    } catch (error) {
      throw new Error('Error sending appeal to Touraika')
    }
  }

  const handleMessage = (event: MessageEvent) => {
    if (event?.origin !== TouraikaSource) return

    const data: TouraikaMessage = event?.data

    switch (data?.type) {
      case TouraikaActions.share.action:
        const url = getShareUrl(RoutingPaths.touraika.detail.path(data.tour_id))

        handleShare(url)

        break

      case TouraikaActions.close.action:
        navigate(RoutingPaths.tours.path())
        break

      case TouraikaActions.book.action:
        if (data?.url) {
          openExternalUrl(data.url)
        } else {
          openWhatsappWithMessage(t('whatsapp_message_chat'))
        }

        break
      case TouraikaActions.appeal.action:
        onSentAppeal(data?.session_id)
        break
      default:
        break
    }
  }

  useEffect(() => {
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  const langParam = langMap[lang] ?? 'ru'

  const deviceId = getLocalStore('mTravelDeviceId') || DeviceId

  let linkOpenTouraika = [
    TouraikaOpenUrl + '=' + deviceId,
    `&lang=${langParam}`,
    id ? `&tour_id=${id}` : '',
  ].join('')

  if (userStoreData?.first_name && userStoreData?.last_name) {
    const nameEn = `&name_en=${userStoreData?.first_name || ''}&last_name_en=${userStoreData?.last_name || ''}`

    linkOpenTouraika += nameEn
  }

  if (
    userStoreData?.mbank_orginal_info?.first_name &&
    userStoreData?.mbank_orginal_info?.last_name
  ) {
    const nameRu = `&name_ru=${userStoreData?.mbank_orginal_info?.first_name || ''}&last_name_ru=${userStoreData?.mbank_orginal_info?.last_name || ''}`

    linkOpenTouraika += nameRu
  }

  if (userStoreData?.phone_number) {
    linkOpenTouraika += `&phone_number=${userStoreData?.phone_number || ''}`
  }

  return (
    <div className={clsx('safe-area-top-padding', s.container)}>
      <iframe
        ref={iframe}
        title="Touraika"
        className={s.frame}
        allow="fullscreen"
        src={linkOpenTouraika}
      />
    </div>
  )
})

export default TouraikaPage
